import React from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch } from "react-redux";
import { Btn } from "../../../shared/Btn";
import { openModal } from "../../../redux/reducers/ModalManager";
import { sendGAEvent } from "../../../utils/utils";

type Props = {
  show: boolean;
  pagesUsed: number;
  isInvoice?: boolean;
  onClose: () => void;
};

const PAGE_THRESHOLD = 50;
const INVOICE_PAGE_THRESHOLD = 10;

/**
 * Engaged-value upgrade modal. Fires once per user (gated by localStorage)
 * after a trialing user crosses the page threshold (50 normally, 10 for
 * invoice trials since invoices are ~1 page each) and just completed a job.
 * Goal: prompt a plan choice while the value is fresh, before they bounce
 * off the trial credit pool.
 */
export const EngagedUpgradeModal: React.FC<Props> = ({
  show,
  pagesUsed,
  isInvoice = false,
  onClose,
}) => {
  const dispatch = useDispatch();

  const jobType = isInvoice ? "invoice" : "other";
  const threshold = isInvoice ? INVOICE_PAGE_THRESHOLD : PAGE_THRESHOLD;

  React.useEffect(() => {
    if (show) {
      sendGAEvent({
        action: "paywall_shown",
        category: "Paywall",
        props: {
          trigger: "engaged_upgrade",
          pages_used: pagesUsed,
          job_type: jobType,
          threshold,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleSeePlans = () => {
    sendGAEvent({
      action: "paywall_upgrade_click",
      category: "Paywall",
      props: { trigger: "engaged_upgrade", job_type: jobType, threshold },
    });
    onClose();
    dispatch(openModal("outOfPages", {}));
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton className="border-0 pb-0" />
      <Modal.Body className="text-center px-4 pb-4">
        <div className="mb-3" style={{ fontSize: "2.5rem", color: "#28a745" }}>
          <FontAwesomeIcon icon="check-circle" />
        </div>
        <h4 className="font-weight-bold mb-2">
          {isInvoice
            ? "You're getting real value out of DocuClipper"
            : `You've processed ${pagesUsed} pages on DocuClipper`}
        </h4>
        <p className="text-muted mb-4" style={{ fontSize: "0.95rem" }}>
          Lock in your plan now. Your extracted data stays in your account, no
          rework.
        </p>

        <div
          className="text-left mb-4 px-2"
          style={{
            background: "#f8f9fa",
            borderRadius: 8,
            padding: "12px 16px",
          }}
        >
          {(isInvoice
            ? [
                "Push approved bills into QuickBooks Online or Xero — no manual entry",
                "Route invoices for approval before they sync",
                "Keep all of your extracted invoices and exports",
              ]
            : [
                "Keep all of your extracted data and exports",
                "Save 30% on annual plans",
                "Cancel anytime, no long-term commitment",
              ]
          ).map((benefit, i, arr) => (
            <div key={i} className={i < arr.length - 1 ? "mb-2" : ""}>
              <FontAwesomeIcon
                icon="check"
                className="text-success mr-2"
                style={{ fontSize: "0.85rem" }}
              />
              <span style={{ fontSize: "0.9rem" }}>{benefit}</span>
            </div>
          ))}
        </div>

        <Btn
          variant="primary"
          size="lg"
          className="w-100 mb-2"
          onClick={handleSeePlans}
        >
          See plans
        </Btn>
        <Btn
          variant="link"
          size="sm"
          className="w-100 text-muted"
          onClick={onClose}
        >
          Keep using my trial
        </Btn>
      </Modal.Body>
    </Modal>
  );
};

export default EngagedUpgradeModal;
